import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, QrCode, Smartphone, Copy, CheckCircle, AlertCircle, 
  ArrowLeft, RefreshCw, Key, Lock 
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Badge } from '../../../components/ui/badge';
import { useAuth } from '../../../contexts/AuthContext';
import PadaidLogo from '../../../components/ui/PadaidLogo';

const TwoFactorAuth = () => {
  const { user, get2FAQR, setup2FA } = useAuth();
  const [qrData, setQrData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    fetchQRData();
  }, []);

  const fetchQRData = async () => {
    try {
      setLoading(true);
      const data = await get2FAQR();
      setQrData(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await setup2FA(verificationCode);
      setSuccess('Authentification à double facteur configurée avec succès !');
      setVerificationCode('');
      await fetchQRData(); // Rafraîchir les données
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const copySecret = async () => {
    if (!qrData?.secret) return;
    
    try {
      await navigator.clipboard.writeText(qrData.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch (err) {
      // Fallback pour les navigateurs sans support clipboard
      const textArea = document.createElement('textarea');
      textArea.value = qrData.secret;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    }
  };

  const regenerateSecret = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir régénérer le secret 2FA ? Cela invalidera votre configuration actuelle.')) {
      await fetchQRData();
    }
  };

  if (isInitialLoad && loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-destructive/5 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-destructive mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement de la configuration 2FA...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-destructive/5">
      {/* Header */}
      <header className="bg-card/50 backdrop-blur-sm border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <PadaidLogo size="md" showText={true} />
              <div className="ml-4">
                <div className="flex items-center">
                  <span className="text-sm text-muted-foreground">Espace Fondateur</span>
                  <Badge variant="destructive" className="ml-2">
                    <Shield className="w-3 h-3 mr-1" />
                    Sécurité
                  </Badge>
                </div>
                <h1 className="text-lg font-semibold text-destructive">Authentification Double Facteur</h1>
              </div>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/dashboard/fondateur">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour Dashboard
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Card */}
        <Card className="mb-8 border-destructive/20">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Lock className="mr-2 h-5 w-5 text-destructive" />
              État de la Sécurité 2FA
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {qrData?.verified ? (
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-yellow-500 mr-3" />
                )}
                <div>
                  <p className="font-medium">
                    {qrData?.verified ? 'Authentification 2FA Active' : 'Configuration 2FA Requise'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {qrData?.verified 
                      ? 'Votre compte est protégé par la double authentification'
                      : 'Configurez la 2FA pour sécuriser votre compte fondateur'
                    }
                  </p>
                </div>
              </div>
              <Badge variant={qrData?.verified ? 'default' : 'destructive'}>
                {qrData?.verified ? 'Configuré' : 'Non configuré'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        {error && (
          <Alert className="mb-6" variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-6" variant="default" className="border-green-500/20 bg-green-500/10 text-green-700">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* QR Code Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <QrCode className="mr-2 h-5 w-5" />
                Configuration avec QR Code
              </CardTitle>
              <CardDescription>
                Scannez ce code avec votre application d'authentification
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {qrData?.qr_code && (
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <img 
                      src={qrData.qr_code} 
                      alt="QR Code 2FA"
                      className="w-48 h-48"
                    />
                  </div>
                </div>
              )}

              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  Applications recommandées :
                </p>
                <div className="flex justify-center space-x-2 text-xs">
                  <Badge variant="outline">Google Authenticator</Badge>
                  <Badge variant="outline">Authy</Badge>
                  <Badge variant="outline">Microsoft Authenticator</Badge>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={regenerateSecret}
                className="w-full"
                disabled={loading}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Régénérer QR Code
              </Button>
            </CardContent>
          </Card>

          {/* Manual Setup & Verification */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Key className="mr-2 h-5 w-5" />
                Configuration Manuelle
              </CardTitle>
              <CardDescription>
                Si vous ne pouvez pas scanner le QR code
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Secret Key */}
              <div className="bg-muted/50 p-4 rounded-lg">
                <Label className="text-sm font-medium mb-2 block">
                  Clé secrète à saisir manuellement :
                </Label>
                <div className="flex items-center space-x-2">
                  <code className="bg-background px-3 py-2 rounded text-sm flex-1 break-all font-mono">
                    {qrData?.secret || 'Chargement...'}
                  </code>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={copySecret}
                    className={secretCopied ? "text-green-600" : ""}
                    disabled={!qrData?.secret}
                  >
                    <Copy className="h-4 w-4" />
                    {secretCopied ? "Copié!" : "Copier"}
                  </Button>
                </div>
              </div>

              {/* Verification Form */}
              <form onSubmit={handleVerification} className="space-y-4">
                <div>
                  <Label htmlFor="verificationCode">Code de vérification</Label>
                  <div className="relative">
                    <Smartphone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      id="verificationCode"
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                      required
                      className="pl-10 text-center text-lg tracking-widest"
                      maxLength={6}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Entrez le code à 6 chiffres généré par votre app
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-destructive hover:bg-destructive/90"
                  disabled={loading || verificationCode.length !== 6}
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-destructive-foreground mr-2"></div>
                      Vérification...
                    </div>
                  ) : qrData?.verified ? (
                    'Reconfigurer 2FA'
                  ) : (
                    'Activer 2FA'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Security Instructions */}
        <Card className="mt-8 bg-muted/30">
          <CardHeader>
            <CardTitle className="text-sm">Instructions de Sécurité</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div>
                <h4 className="font-medium mb-2">✅ Bonnes pratiques :</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• Utilisez une app d'authentification fiable</li>
                  <li>• Sauvegardez le code secret dans un lieu sûr</li>
                  <li>• Ne partagez jamais vos codes 2FA</li>
                  <li>• Testez la configuration avant de finaliser</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">⚠️ Important :</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• La 2FA est obligatoire pour les fondateurs</li>
                  <li>• Régénérer le secret invalide l'ancienne config</li>
                  <li>• Gardez votre téléphone accessible pour les connexions</li>
                  <li>• Contactez le support en cas de problème</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TwoFactorAuth;